import React, { useMemo, useState } from 'react';

import { Link as RouterLink } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { SEARCH_DEBOUNCE_MS } from '~/consts/tours';

import { useGetSuppliers } from '~/hooks/apis/tours/useTourSuppliers';
import useDebounced from '~/hooks/useDebounced';

import SupplierFormDialog from '../../Suppliers/SupplierFormDialog';
import { supplierPath } from '../../nativeTourModel';

export default function ToursSuppliersPage() {
  const [nameInput, setNameInput] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const name = useDebounced(nameInput.trim(), SEARCH_DEBOUNCE_MS);

  const { data: suppliers = [], isLoading, error } = useGetSuppliers(name);

  const hasResults = useMemo(() => suppliers.length > 0, [suppliers]);

  return (
    <Container maxWidth="xl">
      <PageHeader title="Suppliers">
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsCreateOpen(true)}>
          Create supplier
        </Button>
      </PageHeader>

      <Stack spacing={2}>
        <TextField
          label="Search by name"
          value={nameInput}
          onChange={(event) => setNameInput(event.target.value)}
          sx={{ maxWidth: 420 }}
        />

        {isLoading && (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        )}

        {!!error && <Alert severity="error">{error.message}</Alert>}

        {!isLoading && !error && !hasResults && (
          <Alert severity="info">
            {name ? `No suppliers match "${name}".` : 'No suppliers yet. Create one to start adding services.'}
          </Alert>
        )}

        {!isLoading && !error && hasResults && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Address</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {suppliers.map((supplier) => (
                <TableRow key={supplier.id} hover>
                  <TableCell>
                    <Link component={RouterLink} to={supplierPath(supplier.id)}>
                      {supplier.name}
                    </Link>
                  </TableCell>
                  <TableCell>{supplier.email}</TableCell>
                  <TableCell>{supplier.phone}</TableCell>
                  <TableCell>{supplier.address}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Stack>

      {isCreateOpen && <SupplierFormDialog open={isCreateOpen} onClose={() => setIsCreateOpen(false)} />}
    </Container>
  );
}
